import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FaHospital,
    FaArrowLeft,
    FaPlus,
    FaUpload,
    FaImage,
    FaMapMarkerAlt,
} from "react-icons/fa";
import {
    showSuccessToast,
    showErrorToast,
} from "../../../shared/utils/toastHelper";
import { useTitle } from "../../../context/TitleContext";
import LocationPicker from "../../../shared/components/LocationPicker";
import axios from "axios";
import { compressFiles } from "../../../shared/utils/performanceUtils";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AddClinic = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        phone: '',
        password: '',
        title_ar: '',
        title_en: '',
        description_ar: '',
        description_en: '',
        logo: null,
        cover: null,
        address_ar: '',
        address_en: '',
        country_id: '',
        government_id: '',
        center_id: '',
        back_license: null,
        front_license: null,
        license_number: '',
        license_expiry: '',
        lat: '',
        lng: ''
    });
    
    const [addingClinic, setAddingClinic] = useState(false);
    const [countries, setCountries] = useState([]);
    const [governments, setGovernments] = useState([]);
    const [centers, setCenters] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingGovernments, setLoadingGovernments] = useState(false);
    const [loadingCenters, setLoadingCenters] = useState(false);
    const [imageProcessing, setImageProcessing] = useState(false);
    const [imagePreviews, setImagePreviews] = useState({
        logo: null,
        cover: null,
        back_license: null,
        front_license: null,
    });
    const requiredImageFields = ["logo", "cover", "back_license", "front_license"];

    useEffect(() => {
        setTitle("إضافة عيادة جديدة");
        fetchCountries();
    }, [setTitle]);

    useEffect(() => {
        if (formData.country_id) {
            fetchGovernments(formData.country_id);
        } else {
            setGovernments([]);
            setFormData(prev => ({ ...prev, government_id: '', center_id: '' }));
        }
    }, [formData.country_id]);

    useEffect(() => {
        if (formData.government_id) {
            fetchCenters(formData.government_id);
        } else {
            setCenters([]);
            setFormData(prev => ({ ...prev, center_id: '' }));
        }
    }, [formData.government_id]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem("adminkhadamat");
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                return parsed.data?.token?.access_token || parsed.data?.access_token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchCountries = async () => {
        try {
            setLoadingCountries(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            const response = await axios.get(`${BASE_URL}/countries`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (response.data.status === "success" || response.data.statusCode === 200) {
                setCountries(response.data.result || response.data.data || []);
            } else {
                throw new Error(response.data.message || "فشل في جلب الدول");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب الدول"
            );
            setCountries([]);
        } finally {
            setLoadingCountries(false);
        }
    };

    const fetchGovernments = async (countryId) => {
        try {
            setLoadingGovernments(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            const response = await axios.get(`${BASE_URL}/country_governs/${countryId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (response.data.status === "success" || response.data.statusCode === 200) {
                setGovernments(response.data.result || response.data.data || []);
            } else {
                throw new Error(response.data.message || "فشل في جلب المحافظات");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب المحافظات"
            );
            setGovernments([]);
        } finally {
            setLoadingGovernments(false);
        }
    };

    const fetchCenters = async (governmentId) => {
        try {
            setLoadingCenters(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            const response = await axios.get(`${BASE_URL}/govern_centers/${governmentId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (response.data.status === "success" || response.data.statusCode === 200) {
                setCenters(response.data.result || response.data.data || []);
            } else {
                throw new Error(response.data.message || "فشل في جلب المراكز");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب المراكز"
            );
            setCenters([]);
        } finally {
            setLoadingCenters(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e, fieldName) => {
        const file = e.target.files[0];

        if (!file) {
            setFormData(prev => ({ ...prev, [fieldName]: null }));
            setImagePreviews(prev => ({ ...prev, [fieldName]: null }));
            return;
        }

        if (!file.type.startsWith("image/")) {
            showErrorToast("يرجى اختيار صورة صالحة");
            e.target.value = "";
            return;
        }

        setImageProcessing(true);

        try {
            const [compressedResult] = await compressFiles([file], {
                maxSize: 800000,
                quality: 0.85,
            });

            const finalFile =
                compressedResult instanceof File
                    ? compressedResult
                    : new File([compressedResult], file.name, {
                          type: file.type || "image/jpeg",
                          lastModified: Date.now(),
                      });

            setFormData(prev => ({ ...prev, [fieldName]: finalFile }));
            const previewUrl = URL.createObjectURL(finalFile);
            setImagePreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
        } catch (error) {
            showErrorToast("فشل في معالجة الصورة، يرجى المحاولة مرة أخرى");
        } finally {
            setImageProcessing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.phone || !formData.password || !formData.title_ar || !formData.country_id || !formData.government_id || !formData.center_id) {
            showErrorToast("يرجى ملء جميع الحقول المطلوبة");
            return;
        }

        if (imageProcessing) {
            showErrorToast("يرجى الانتظار حتى يتم تجهيز الصور قبل الإرسال");
            return;
        }

        try {
            setAddingClinic(true);
            const token = getAuthToken();
            
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            const phoneWithPrefix = `+20${formData.phone}`;
            const formDataToSend = new FormData();
            
            formDataToSend.append('phone', phoneWithPrefix);
            formDataToSend.append('password', formData.password);
            formDataToSend.append('name', formData.title_ar);
            formDataToSend.append('title_ar', formData.title_ar);
            formDataToSend.append('title_en', formData.title_en || '');
            formDataToSend.append('description_ar', formData.description_ar || '');
            formDataToSend.append('description_en', formData.description_en || '');
            formDataToSend.append('address_ar', formData.address_ar || '');
            formDataToSend.append('address_en', formData.address_en || '');
            formDataToSend.append('country_id', formData.country_id);
            formDataToSend.append('government_id', formData.government_id);
            formDataToSend.append('center_id', formData.center_id);
            formDataToSend.append('license_number', formData.license_number || '');
            formDataToSend.append('license_expiry', formData.license_expiry || '');
            formDataToSend.append('lat', formData.lat || '');
            formDataToSend.append('lng', formData.lng || '');

            if (formData.logo) {
                formDataToSend.append('logo', formData.logo);
            }
            if (formData.cover) {
                formDataToSend.append('cover', formData.cover);
            }
            if (formData.back_license) {
                formDataToSend.append('back_license', formData.back_license);
            }
            if (formData.front_license) {
                formDataToSend.append('front_license', formData.front_license);
            }

            const response = await axios.post(
                `${BASE_URL}/clinics/regist`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                    },
                }
            );

            if (response.data.status === "success" || response.data.statusCode === 200) {
                showSuccessToast("تم إضافة العيادة بنجاح");
                navigate('/admin-clinics');
            } else {
                throw new Error(response.data.message || "فشل في إضافة العيادة");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في إضافة العيادة"
            );
        } finally {
            setAddingClinic(false);
        }
    };

    return (
        <div className="main_content">
            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0" style={{ fontSize: "20px" }}>
                        <FaHospital className="mx-2" style={{ color: "#184194" }} />
                        إضافة عيادة جديدة
                    </h2>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/admin-clinics')}
                        style={{ borderRadius: '8px' }}
                    >
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                </div>

                {/* Form */}
                <div className="row justify-content-center">
                    <div className="col-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="card shadow"
                            style={{ borderRadius: '12px' }}
                        >
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        {/* Basic Info */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">اسم العيادة بالعربية *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title_ar"
                                                value={formData.title_ar}
                                                onChange={handleInputChange}
                                                placeholder="أدخل اسم العيادة بالعربية"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">اسم العيادة بالإنجليزية</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title_en"
                                                value={formData.title_en}
                                                onChange={handleInputChange}
                                                placeholder="أدخل اسم العيادة بالإنجليزية"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">رقم الهاتف *</label>
                                            <div className="input-group" style={{ direction: "ltr" }}>
                                                <span className="input-group-text" style={{
                                                    fontSize: '16px',
                                                    fontWeight: '500',
                                                    background: '#f8f9fa',
                                                    color: '#6c757d',
                                                    border: '2px solid #e2e8f0'
                                                }}>
                                                    +20
                                                </span>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="1xxxxx"
                                                    style={{
                                                        borderLeft: 'none',
                                                        borderRadius: '0 12px 12px 0',
                                                        border: '2px solid #e2e8f0',
                                                        padding: '12px 16px',
                                                        fontSize: '16px'
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">كلمة المرور *</label>
                                            <input
                                                type="password"
                                                className="form-control"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                placeholder="أدخل كلمة المرور"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الوصف بالعربية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                name="description_ar"
                                                value={formData.description_ar}
                                                onChange={handleInputChange}
                                                placeholder="أدخل الوصف بالعربية"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الوصف بالإنجليزية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                name="description_en"
                                                value={formData.description_en}
                                                onChange={handleInputChange}
                                                placeholder="أدخل الوصف بالإنجليزية"
                                            />
                                        </div>

                                        {/* Location */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الدولة *</label>
                                            <select
                                                className="form-select"
                                                name="country_id"
                                                value={formData.country_id}
                                                onChange={handleInputChange}
                                                required
                                                disabled={loadingCountries}
                                            >
                                                <option value="">اختر الدولة</option>
                                                {countries.map((country) => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name?.ar || country.title?.ar || country.name || country.title || `#${country.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingCountries && <small className="text-muted">جاري التحميل...</small>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">المحافظة *</label>
                                            <select
                                                className="form-select"
                                                name="government_id"
                                                value={formData.government_id}
                                                onChange={handleInputChange}
                                                required
                                                disabled={!formData.country_id || loadingGovernments}
                                            >
                                                <option value="">
                                                    {!formData.country_id ? 'اختر الدولة أولاً' : 'اختر المحافظة'}
                                                </option>
                                                {governments.map((gov) => (
                                                    <option key={gov.id} value={gov.id}>
                                                        {gov.name?.ar || gov.title?.ar || gov.name || gov.title || `#${gov.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingGovernments && <small className="text-muted">جاري التحميل...</small>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">المنطقة</label>
                                            <select
                                                className="form-select"
                                                name="center_id"
                                                value={formData.center_id}
                                                onChange={handleInputChange}
                                                required
                                                disabled={!formData.government_id || loadingCenters}
                                            >
                                                <option value="">
                                                    {!formData.government_id ? 'اختر المنطقة ' : 'اختر المنطقة'}
                                                </option>
                                                {centers.map((center) => (
                                                    <option key={center.id} value={center.id}>
                                                        {center.name?.ar || center.title?.ar || center.name || center.title || `#${center.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingCenters && <small className="text-muted">جاري التحميل...</small>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">العنوان بالعربية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                name="address_ar"
                                                value={formData.address_ar}
                                                onChange={handleInputChange}
                                                placeholder="أدخل عنوان العيادة بالعربية"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">العنوان بالإنجليزية</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                name="address_en"
                                                value={formData.address_en}
                                                onChange={handleInputChange}
                                                placeholder="أدخل عنوان العيادة بالإنجليزية"
                                            />
                                        </div>

                                        {/* License */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">رقم الترخيص</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="license_number"
                                                value={formData.license_number}
                                                onChange={handleInputChange}
                                                placeholder="أدخل رقم الترخيص"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">تاريخ انتهاء الترخيص</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="license_expiry"
                                                value={formData.license_expiry}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        {/* Images */}
                                    <div className="col-md-6 mb-3">
                                            <label className="form-label">الشعار</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                onChange={(e) => handleFileChange(e, 'logo')}
                                                disabled={imageProcessing}
                                            />
                                            {imagePreviews.logo && (
                                                <div className="mt-2 text-center">
                                                    <img
                                                        src={imagePreviews.logo}
                                                        alt="معاينة الشعار"
                                                        style={{ maxWidth: '200px', maxHeight: '180px', objectFit: 'contain', borderRadius: '12px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">صورة الغلاف</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                onChange={(e) => handleFileChange(e, 'cover')}
                                                disabled={imageProcessing}
                                            />
                                            {imagePreviews.cover && (
                                                <div className="mt-2 text-center">
                                                    <img
                                                        src={imagePreviews.cover}
                                                        alt="معاينة الغلاف"
                                                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '12px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الرخصه من الخلف</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                onChange={(e) => handleFileChange(e, 'back_license')}
                                                disabled={imageProcessing}
                                            />
                                            {imagePreviews.back_license && (
                                                <div className="mt-2 text-center">
                                                    <img
                                                        src={imagePreviews.back_license}
                                                        alt="معاينة الرخصه من الخلف"
                                                        style={{ maxWidth: '200px', maxHeight: '180px', objectFit: 'cover', borderRadius: '12px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">الرخصة من الأمام</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                onChange={(e) => handleFileChange(e, 'front_license')}
                                                disabled={imageProcessing}
                                            />
                                            {imagePreviews.front_license && (
                                                <div className="mt-2 text-center">
                                                    <img
                                                        src={imagePreviews.front_license}
                                                        alt="معاينة الرخصة من الأمام"
                                                        style={{ maxWidth: '200px', maxHeight: '180px', objectFit: 'cover', borderRadius: '12px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Map */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label">اختر الموقع على الخريطة</label>
                                            <LocationPicker
                                                lat={formData.lat}
                                                lng={formData.lng}
                                                onLocationChange={(lat, lng) => {
                                                    setFormData(prev => ({ ...prev, lat: lat.toString(), lng: lng.toString() }));
                                                }}
                                                height="300px"
                                            />
                                            {formData.lat && formData.lng && (
                                                <small className="text-muted d-block mt-2">
                                                    الإحداثيات: {formData.lat}, {formData.lng}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    {imageProcessing && (
                                        <div className="alert alert-info py-2 px-3 mt-3">
                                            جاري تجهيز الصور، يرجى الانتظار...
                                        </div>
                                    )}
                                    {/* Submit Buttons */}
                                    <div className="d-flex justify-content-center gap-2 mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => navigate('/admin-clinics')}
                                            disabled={addingClinic}
                                        >
                                            إلغاء
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn text-white"
                                            style={{ backgroundColor: '#184194' }}
                                            disabled={
                                                addingClinic ||
                                                imageProcessing ||
                                                !requiredImageFields.every((field) => formData[field])
                                            }
                                        >
                                            {addingClinic ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    جاري الإضافة...
                                                </>
                                            ) : (
                                                'إضافة'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddClinic;

